import { PiAddressBookDuotone } from 'react-icons/pi';

import {
  FCompositionNode,
  FNodeChild,
  FNodeTypesEnum,
  SchemaTypesEnum,
} from '@spazious/dollhouse-fsetup';
import { useTranslate } from '@spazious/dollhouse-i18n';
import {
  Flex,
  List,
  ListItem,
  Panel,
  PanelContent,
  PanelHeader,
  Typography,
} from '@spazious/dollhouse-uikit';

import { InspectorFArrangement } from './InspectorFArrangement.js';

const fSchemaMapping = Object.fromEntries(
  Object.entries(SchemaTypesEnum).map(([key, value]) => [value, key])
);

const formatMetas = (meta: any) =>
  Object.entries(meta ?? {})
    .reduce((acc, [key, value]) => [...acc, `${key}: ${value}`], [] as string[])
    .join(', ');

const nodeTitle = (node: FCompositionNode) => {
  return fSchemaMapping[node.id & 0xff]?.replace(/^(F(Arrangement|Ensemble))/, '') ?? '';
};

export const UnlinkNodesConfirmationForm: React.FC<{
  nodes: FNodeChild[];
}> = ({ nodes }) => {
  const translate = useTranslate();

  const compositions = [
    ...nodes.reduce((acc, node) => {
      if (
        node.type === FNodeTypesEnum.Furniture &&
        node.originator?.type === FNodeTypesEnum.FComposition
      ) {
        // individual furnitures selected in composition
        acc.add(node.originator);
      } else if (node.type === FNodeTypesEnum.FComposition) {
        // all furnitures in selected composition
        acc.add(node);
      }
      return acc;
    }, new Set<FCompositionNode>()),
  ];

  const nodeLabel = (node: FCompositionNode) => {
    // i18n-extract-disable
    const name = translate(nodeTitle(node));
    let chairs = 0,
      tables = 0;
    node.traverse((node) => {
      if (node.type === FNodeTypesEnum.Furniture && node.visible !== false) {
        if (/table/.test(node.furnitureType)) {
          tables++;
        } else if (/chair/.test(node.furnitureType)) {
          chairs++;
        }
      }
    });

    if (chairs && tables) {
      return `${name} ${translate('with')} ${chairs} ${translate('chairs')} ${translate(
        'and'
      )} ${tables} ${translate('tables')}`;
    } else if (tables) {
      return `${name} ${translate('with')} ${tables} ${translate('tables')}`;
    } else if (chairs) {
      return `${name} ${translate('with')} ${chairs} ${translate('chairs')}`;
    }
    return name;
  };

  const title =
    compositions.length === 1
      ? translate(
          'By unlinking, you will not be able to change parameters for this setup again, are you sure?'
        )
      : translate(
          'By unlinking, you will not be able to change the parameters for these setups again, are you sure?'
        );

  return (
    <Flex column gap="calc(var(--spacing-gap) * 4)">
      {!!compositions.length && (
        <Flex column gap="var(--spacing-gap)">
          <Typography variant="title">{title}</Typography>
          {compositions.length > 1 ? (
            <List>
              {compositions.map((node) => (
                <ListItem
                  key={node.uuid}
                  avatar={<PiAddressBookDuotone />}
                  subtitle={formatMetas(node.meta)}
                  title={nodeLabel(node)}
                />
              ))}
            </List>
          ) : (
            <Panel>
              <PanelHeader>
                <Flex gap="var(--spacing-padding)">
                  <Typography>{nodeLabel(compositions[0])}</Typography>
                </Flex>
              </PanelHeader>
              <PanelContent>
                <InspectorFArrangement editable={false} node={compositions[0]} />
              </PanelContent>
            </Panel>
          )}
        </Flex>
      )}
    </Flex>
  );
};
